import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { ProjectDetail } from "@/modules/management/project/domain/types";
import { projectStatusAccentTextClass } from "@/modules/management/project/presentation/lib/project-status-progress-styles";
import { ProjectProgressBar } from "@/modules/management/project/presentation/ui/ProjectProgressBar";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDate } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  confirm,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  AlignLeft,
  Calendar,
  HelpCircle,
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { managementProjectApi } from "../api/client";
import {
  useProjectById,
  useProjectRisks,
  useProjectTasks,
} from "../api/queries";
import {
  ProjectDetailTour,
  useProjectDetailTour,
} from "../tour/ProjectDetailTour";
import { ProjectDetailRisksTab } from "../ui/ProjectDetailRisksTab";
import { ProjectDetailTasksTab } from "../ui/ProjectDetailTasksTab";
import { ProjectDetailTimelineTab } from "../ui/ProjectDetailTimelineTab";

function projectStatusLabel(status: string) {
  switch (status) {
    case "active":
      return "ກຳລັງດຳເນີນງານ";
    case "completed":
      return "ສຳເລັດ";
    case "on_hold":
      return "ຢຸດຊົ່ວຄາວ";
    default:
      return status;
  }
}

export function ProjectDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const nav = useNavigate();
  const tour = useProjectDetailTour();
  const projectQuery = useProjectById(id);
  const project = projectQuery.data;
  const permissions = usePermissions();
  const canUpdate = permissions.has(Permissions.mgmt_project.update);

  if (projectQuery.isLoading || !id) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (projectQuery.isError || !project) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <p className="text-muted-foreground text-sm">ບໍ່ພົບໂຄງການ</p>
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/management/projects",
                  search: {
                    ...listSearch,
                  },
                })
              }
            >
              ກັບໄປລາຍການໂຄງການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const canDelete = permissions.has(Permissions.mgmt_project.delete);
  return (
    <>
      <ProjectDetailTour
        run={tour.run}
        onCallback={tour.handleJoyrideCallback}
      />
      <ProjectDetailContent
        project={project}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onStartTour={tour.startTour}
      />
    </>
  );
}

const listSearch = {
  offset: 0,
  limit: 20,
  sort: undefined as Array<{ field: string; dir: "asc" | "desc" }> | undefined,
  status: undefined as string | undefined,
  level: undefined as string | undefined,
  responsibleUserId: undefined as string | undefined,
  startDateFrom: undefined as string | undefined,
  startDateTo: undefined as string | undefined,
  endDateFrom: undefined as string | undefined,
  endDateTo: undefined as string | undefined,
  search: undefined as string | undefined,
};

function projectLevelLabel(project: ProjectDetail): string {
  const level = project.level ?? "organization";
  if (level === "position" && project.positionName) return project.positionName;
  if (level === "department" && project.departmentName)
    return project.departmentName;
  switch (level) {
    case "position":
      return "ລະດັບຕຳແໜ່ງ";
    case "department":
      return "ລະດັບຜະແນກ";
    default:
      return "ລະດັບອົງກອນ";
  }
}

function ProjectDetailContent({
  project,
  canUpdate,
  canDelete,
  onStartTour,
}: {
  project: ProjectDetail;
  canUpdate: boolean;
  canDelete: boolean;
  onStartTour: () => void;
}) {
  const nav = useNavigate();
  const projectId = project.id;
  const [activeTab, setActiveTab] = useState("tasks");
  const [openTaskDialogFromHeader, setOpenTaskDialogFromHeader] =
    useState(false);
  const [openRiskDialogFromBottom, setOpenRiskDialogFromBottom] =
    useState(false);

  const handleDeleteProject = async () => {
    const ok = await confirm({
      title: "ລຶບໂຄງການ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບໂຄງການ "${project.name}"? ວຽກຍ່ອຍ ແລະ ຄວາມສຽງທັງໝົດຈະຖືກລຶບຕາມ. ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await managementProjectApi.deleteProject(projectId);
      toast.success("ລຶບໂຄງການສຳເລັດ");
      nav({ to: "/app/management/projects", search: listSearch });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  return (
    <>
      <Header />
      <Main>
        {/* Title + actions */}
        <div
          className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          data-tourid="detail-header"
        >
          <div className="flex items-center gap-2">
            <h1 className="font-bold text-2xl tracking-tight">
              {project.name}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={onStartTour}
              aria-label="ເລີ່ມທົດລອງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
          <div className="flex gap-2">
            {canUpdate && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    nav({
                      to: "/app/management/projects/$id/edit",
                      params: { id: projectId },
                    })
                  }
                  className="gap-1.5"
                >
                  <Pencil className="h-4 w-4" />
                  ແກ້ໄຂ
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setActiveTab("tasks");
                    setOpenTaskDialogFromHeader(true);
                  }}
                  className="gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  ເພີ່ມວຽກຍ່ອຍ
                </Button>
              </>
            )}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteProject}
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                ລຶບໂຄງການ
              </Button>
            )}
          </div>
        </div>

        <Card className="mb-6" data-tourid="detail-description">
          <CardContent>
            <div className="flex items-start gap-2">
              <AlignLeft className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  ຄຳອະທິບາຍໂຄງການ
                </p>
                {project.description?.trim() ? (
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">
                    {project.description.trim()}
                  </p>
                ) : (
                  <p className="mt-2 text-muted-foreground text-sm italic">
                    ບໍ່ມີຄຳອະທິບາຍ
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards row: Status, Completion, Team, Open Risks */}
        <div
          className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          data-tourid="detail-cards"
        >
          <Card>
            <CardContent className="pt-4">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ສະຖານະໂຄງການ
              </p>
              <p className="mt-1 font-medium">
                {projectStatusLabel(project.status)}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                {projectLevelLabel(project)}
              </p>
              <div className="mt-2 flex items-center gap-2 text-muted-foreground text-xs">
                <Calendar className="h-3.5 w-3.5" />
                <span>
                  {project.startDate ? formatDate(project.startDate) : "—"} –{" "}
                  {project.endDate ? formatDate(project.endDate) : "—"}
                </span>
              </div>
              <ProjectProgressBar
                status={project.status}
                value={project.progressPercent}
                className="mt-2 h-2"
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ຄວາມຄືບໜ້າ
              </p>
              <p
                className={cn(
                  "mt-1 font-bold text-2xl",
                  projectStatusAccentTextClass(project.status),
                )}
              >
                {project.progressPercent}%
              </p>
              <ProjectProgressBar
                status={project.status}
                value={project.progressPercent}
                className="mt-2 h-2"
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ສະມາຊິກທີມ
              </p>
              <div className="mt-1 flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <ProjectTeamCount projectId={projectId} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ຄວາມສຽງທີ່ເປີດ
              </p>
              <div className="mt-1 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <OpenRisksCount projectId={projectId} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Tasks, Risks, Timeline */}
        <div data-tourid="detail-tabs">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-4"
          >
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="tasks">ວຽກຍ່ອຍ</TabsTrigger>
              <TabsTrigger value="risks">ຄວາມສຽງ</TabsTrigger>
              <TabsTrigger value="timeline">ໄລຍະເວລາ</TabsTrigger>
            </TabsList>
            <TabsContent value="tasks" className="space-y-4">
              <ProjectDetailTasksTab
                projectId={projectId}
                projectLevel={project.level ?? "organization"}
                projectPositionId={project.positionId ?? undefined}
                projectDepartmentId={project.departmentId ?? undefined}
                canUpdate={canUpdate}
                openTaskDialogRequested={openTaskDialogFromHeader}
                onOpenTaskDialogConsumed={() =>
                  setOpenTaskDialogFromHeader(false)
                }
              />
            </TabsContent>
            <TabsContent value="risks" className="space-y-4">
              <ProjectDetailRisksTab
                projectId={projectId}
                canUpdate={canUpdate}
                openRiskDialogRequested={openRiskDialogFromBottom}
                onOpenRiskDialogConsumed={() =>
                  setOpenRiskDialogFromBottom(false)
                }
              />
            </TabsContent>
            <TabsContent value="timeline" className="space-y-4">
              <ProjectDetailTimelineTab project={project} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Bottom: Active Risks + Next Milestone */}
        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_340px]">
          <Card data-tourid="detail-open-risks">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">ຄວາມສຽງທີ່ກຳລັງເປີດ</h3>
                {canUpdate && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveTab("risks");
                      setOpenRiskDialogFromBottom(true);
                    }}
                    className="gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    ເພີ່ມຄວາມສຽງ
                  </Button>
                )}
              </div>
              <ActiveRisksList projectId={projectId} />
            </CardContent>
          </Card>
          <Card
            className="border-primary/30 bg-primary/5"
            data-tourid="detail-next-milestone"
          >
            <CardContent className="pt-4">
              <h3 className="font-semibold">ຈຸດໝາຍຖັດໄປ</h3>
              <NextMilestoneCard project={project} />
              {canUpdate && (
                <Button
                  variant="default"
                  size="sm"
                  className="mt-4 gap-1"
                  onClick={() => {
                    setActiveTab("tasks");
                    setOpenTaskDialogFromHeader(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  ສ້າງວຽກຍ່ອຍ
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}

function ProjectTeamCount({ projectId }: { projectId: string }) {
  const tasksQuery = useProjectTasks(projectId);
  const tasks = tasksQuery.data ?? [];
  const assigneeIds = new Set(
    tasks.map((t) => t.assigneeId).filter(Boolean),
  ) as Set<string>;
  const count = assigneeIds.size || 1;
  return <span>{count} ຜູ້ປະກອບສ່ວນ</span>;
}

function OpenRisksCount({ projectId }: { projectId: string }) {
  const risksQuery = useProjectRisks(projectId);
  const risks = risksQuery.data ?? [];
  const openCount = risks.filter((r) => r.status === "open").length;
  const criticalCount = risks.filter(
    (r) => r.status === "open" && r.severity === "high",
  ).length;
  if (openCount === 0) return <span>0</span>;
  return (
    <span>{criticalCount > 0 ? `${criticalCount} ສູງ` : openCount} ຄວາມສຽງ</span>
  );
}

function ActiveRisksList({ projectId }: { projectId: string }) {
  const risksQuery = useProjectRisks(projectId);
  const risks = risksQuery.data ?? [];
  const openRisks = risks.filter((r) => r.status === "open").slice(0, 5);
  if (openRisks.length === 0) {
    return <p className="py-4 text-muted-foreground text-sm">ບໍ່ມີຄວາມສຽງທີ່ເປີດ</p>;
  }
  return (
    <ul className="mt-3 space-y-2">
      {openRisks.map((r) => (
        <li
          key={r.id}
          className="flex items-start gap-2 rounded-lg border p-2 text-sm"
        >
          <span
            className={cn(
              "mt-1.5 h-2 w-2 shrink-0 rounded-full",
              r.severity === "high" && "bg-destructive",
              r.severity === "medium" && "bg-yellow-500",
              r.severity === "low" && "bg-muted-foreground",
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{r.description}</p>
            <Badge variant="outline" className="mt-1 text-xs">
              {r.severity === "high"
                ? "ສູງ"
                : r.severity === "medium"
                  ? "ກາງ"
                  : "ຕ່ຳ"}
            </Badge>
          </div>
        </li>
      ))}
    </ul>
  );
}

function NextMilestoneCard({ project }: { project: ProjectDetail }) {
  const phases = project.phases ?? [];
  const withDue = phases.filter((p): p is typeof p & { dueDate: string } =>
    Boolean(p.dueDate),
  );
  const next = withDue.sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
  )[0];
  if (!next) {
    return (
      <p className="mt-2 text-muted-foreground text-sm">ບໍ່ມີຈຸດໝາຍທີ່ກຳນົດວັນທີ່</p>
    );
  }
  const dueDate = next.dueDate;
  const daysLeft = Math.ceil(
    (new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );
  return (
    <div className="mt-2">
      <p className="font-medium">{next.name}</p>
      <p className="mt-1 text-muted-foreground text-sm">
        ວັນກຳນົດ: {formatDate(dueDate)}
      </p>
      {next.assigneeName && (
        <p className="mt-1 text-muted-foreground text-sm">
          ມອບໝາຍ: {next.assigneeName}
        </p>
      )}
      <p className="mt-1 flex items-center gap-1 text-muted-foreground text-xs">
        <Calendar className="h-3.5 w-3.5" />
        {daysLeft > 0 ? `${daysLeft} ວັນຄົງເຫຼືອ` : "ຜ່ານມາແລ້ວ"}
      </p>
    </div>
  );
}
